/**
 * Progress-note AI generation flow rendered inside the AI Assistant Drawer.
 * Asks for optional session context, then calls page-owned generate/regenerate handlers.
 */

import {
  ActionIcon,
  Box,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { useRef, useState } from 'react';

import { ChatMessageBubble } from './ChatMessageBubble';
import { useScrollLatestChatToTop } from './useScrollLatestChatToTop';
import type { ProgressNoteContext } from './types';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

export interface ProgressNoteGenerationFlowProps {
  context: ProgressNoteContext;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'error';
  text: string;
}

export const PROGRESS_NOTE_INTRO_MESSAGE =
  "Do you want to tell me anything else about the session? You can share extra context or say there's "
  + "nothing extra and I'll generate the note.";

export const PROGRESS_NOTE_REVISE_INTRO_MESSAGE =
  'There is already a draft in the note form. Tell me what to revise and I’ll regenerate it.';

const DRAFT_READY_MESSAGE =
  'I drafted the progress note in the form. Review it there, or tell me what to revise and I’ll regenerate.';

const NOTHING_EXTRA_RE =
  /^(nothing|nothing extra|no|n\/a|na|generate|just generate|go ahead|none|nope)$/i;

let idCounter = 0;
function nextId(role: string): string {
  idCounter += 1;
  return `${role}-${idCounter}`;
}

function resolveAdditionalPrompt(userText: string): string {
  const trimmed = userText.trim();
  if (!trimmed || NOTHING_EXTRA_RE.test(trimmed)) return '';
  return trimmed;
}

export function ProgressNoteGenerationFlow({
  context,
}: ProgressNoteGenerationFlowProps): JSX.Element {
  const { geminiAiDisabled, canGenerate, canRegenerate, onGenerate, onRegenerate } = context;
  // Opening on a note that already has a draft (e.g. after a reload) resumes in revise mode.
  const [draftReady, setDraftReady] = useState(() => canRegenerate);
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: nextId('assistant'),
      role: 'assistant',
      text: canRegenerate ? PROGRESS_NOTE_REVISE_INTRO_MESSAGE : PROGRESS_NOTE_INTRO_MESSAGE,
    },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlightRef = useRef(false);

  const latestMessageKey = messages.length > 0 ? messages[messages.length - 1].id : undefined;
  const { viewportRef, latestMessageRef } = useScrollLatestChatToTop(
    messages.length,
    latestMessageKey,
  );

  const canSend =
    !geminiAiDisabled
    && !busy
    && input.trim().length > 0
    && (draftReady ? canRegenerate : canGenerate);

  const sendMessage = async (content: string): Promise<void> => {
    const trimmed = content.trim();
    if (!trimmed || inFlightRef.current || geminiAiDisabled) return;
    if (draftReady ? !canRegenerate : !canGenerate) return;

    inFlightRef.current = true;
    setBusy(true);
    setInput('');
    const userMessage: ChatMessage = { id: nextId('user'), role: 'user', text: trimmed };
    setMessages((prev) => [...prev, userMessage]);

    try {
      if (!draftReady) {
        await onGenerate(resolveAdditionalPrompt(trimmed));
        setDraftReady(true);
        setMessages((prev) => [
          ...prev,
          { id: nextId('assistant'), role: 'assistant', text: DRAFT_READY_MESSAGE },
        ]);
      } else {
        await onRegenerate(trimmed);
        setMessages((prev) => [
          ...prev,
          {
            id: nextId('assistant'),
            role: 'assistant',
            text: 'I’ve regenerated the draft with your feedback. Review the note form and tell me if you want further changes.',
          },
        ]);
      }
    } catch (err) {
      const message = getUserFacingErrorMessage(err, 'Something went wrong. Please try again.');
      setMessages((prev) => [
        ...prev,
        { id: nextId('error'), role: 'error', text: message },
      ]);
    } finally {
      setBusy(false);
      inFlightRef.current = false;
    }
  };

  const placeholder = draftReady
    ? 'Describe what to revise… (Enter to send)'
    : 'Extra session context, or “nothing”… (Enter to send)';

  return (
    <Stack gap={0} h="100%" style={{ minHeight: 0 }}>
      <ScrollArea type="auto" viewportRef={viewportRef} style={{ flex: 1, minHeight: 0 }}>
        <Stack gap="md" p="md" role="log" aria-label="Progress note AI conversation">
          {messages.map((message, index) => (
            <ChatMessageBubble
              key={message.id}
              ref={index === messages.length - 1 ? latestMessageRef : undefined}
              role={message.role}
              text={message.text}
            />
          ))}
          {busy && (
            <Group gap="xs" px="xs">
              <Loader size="xs" color="gold" />
              <Text size="sm" c="dimmed">
                {draftReady ? 'Regenerating note…' : 'Generating note…'}
              </Text>
            </Group>
          )}
        </Stack>
      </ScrollArea>

      <Box
        p="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          background: 'var(--mantine-color-white)',
          flexShrink: 0,
        }}
      >
        {geminiAiDisabled ? (
          <Text size="sm" c="dimmed">
            AI documentation is disabled for this client or provider.
          </Text>
        ) : (
          <Group gap="xs" align="flex-end" wrap="nowrap">
            <Textarea
              aria-label="Message about the progress note"
              placeholder={placeholder}
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void sendMessage(input);
                }
              }}
              autosize
              minRows={2}
              maxRows={6}
              disabled={busy}
              style={{ flex: 1 }}
              styles={{ input: { borderRadius: 10 } }}
            />
            <ActionIcon
              size="lg"
              color="gold"
              variant="filled"
              onClick={() => void sendMessage(input)}
              disabled={!canSend}
              aria-label="Send message"
            >
              <IconSend size={16} />
            </ActionIcon>
          </Group>
        )}
      </Box>
    </Stack>
  );
}
